import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricSeriesSpec } from '@/components/charts/generic/seriesTypes';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { DomainBioscopeDay, DomainBioscopeDayMetric } from '@/domain/cages/overview.types';
import type { Locale } from '@/utils/localeFormatters';

// ---------------------------------------------------------------------------
// Depth and temperature
// ---------------------------------------------------------------------------

export interface DepthTemperatureChartSpecParams {
    /** Carries both depth and temperature, so the chart has a single source. */
    days: DomainBioscopeDay[];
    /** The daypart whose readings to plot. */
    daypartTag: string;
    /** All already translated. */
    depthLabel: string;
    temperatureLabel: string;
    minLabel: string;
    maxLabel: string;
    averageLabel: string;
    locale: Locale;
}

const DEPTH_UNIT = 'm';
const TEMPERATURE_UNIT = '°C';
const DEPTH_SUGGESTED_MAX = 5;
const TEMPERATURE_SUGGESTED_MAX = 20;
const TEMPERATURE_STEP_SIZE = 5;
const DEPTH_TEMPERATURE_VALUE_DECIMALS = 2;

/**
 * Bioscope depth on an inverted left axis, water temperature on the right.
 *
 * The depth axis is reversed because deeper readings belong lower on the chart.
 * The min and max bands start collapsed, matching the legacy chart — they are
 * context for the average rather than three equally important series.
 *
 * The day axis comes from the overview alone, so this chart spans exactly the
 * range every other chart on the page does. Temperature is fetched from its own
 * endpoint, which returns the cage's whole history rather than a range; joining it
 * onto this axis is what narrows it, and readings outside the range are dropped.
 */
export function buildDepthTemperatureChartSpec(
    params: DepthTemperatureChartSpecParams
): MetricChartSpec<'line'> {
    const {
        days,
        daypartTag,
        depthLabel,
        temperatureLabel,
        minLabel,
        maxLabel,
        averageLabel,
        locale,
    } = params;

    const depthDays = days.filter((day) => day.daypartTag === daypartTag);

    function depthSeries(
        key: DomainBioscopeDayMetric,
        label: string,
        color: string,
        hiddenByDefault: boolean
    ): MetricSeriesSpec {
        return {
            key,
            label,
            color,
            hiddenByDefault,
            data: depthDays.map((day) => ({ day: day.day, value: day.data[key] })),
        };
    }

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: [
            depthSeries('depthMin', minLabel, graphSettings.minMaxAvgColors.min, true),
            depthSeries('depthMax', maxLabel, graphSettings.minMaxAvgColors.max, true),
            depthSeries('depthAvg', averageLabel, graphSettings.minMaxAvgColors.avg, false),
            {
                key: 'temperature',
                label: temperatureLabel,
                color: graphSettings.graphColors.temperature,
                axis: 'right',
                fill: true,
                // The same backdrop as the lice charts' temperature band.
                fillGradient: true,
                // Temperature ships with the camera telemetry, so it shares the
                // depth rows and the chart's own date range. It used to come from
                // the lice controller, whose endpoint returns the whole history and
                // stretched this axis well past the selected range.
                data: depthDays.map((day) => ({
                    day: day.day,
                    value: day.data.temperature,
                })),
            },
        ],
        left: {
            label: depthLabel,
            unit: DEPTH_UNIT,
            reverse: true,
            beginAtZero: true,
            suggestedMax: DEPTH_SUGGESTED_MAX,
            tickDecimals: 0,
            valueDecimals: DEPTH_TEMPERATURE_VALUE_DECIMALS,
        },
        right: {
            label: temperatureLabel,
            unit: TEMPERATURE_UNIT,
            beginAtZero: true,
            suggestedMax: TEMPERATURE_SUGGESTED_MAX,
            stepSize: TEMPERATURE_STEP_SIZE,
            tickDecimals: 0,
            valueDecimals: DEPTH_TEMPERATURE_VALUE_DECIMALS,
        },
        locale,
    };
}
